import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import { useCore } from '../hooks/useCore';
import { usePools } from '../hooks/usePools';
import Loader from '../components/Loader';
import config from '../config/network';
import CoreABI from '../abi/ClusterCore.json';
import PoolsABI from '../abi/ClusterPools.json';
import MatrixABI from '../abi/ClusterMatrix.json';
import { formatUSDTRaw, shortenAddress, POOL_LABELS, PACKAGE_LABELS, PACKAGE_NAMES, getErrorMessage } from '../utils/helpers';
import '../styles/admin.css';

export default function Admin({ onNotify }) {
  const { account, provider, signer, isAdmin, isOwner } = useWeb3();
  const { getUserInfo, getCoreRead } = useCore();
  const { getAllPoolInfo, getWeeklyRoyaltyInfo, getMonthlyRoyaltyInfo } = usePools();

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [contractBalance, setContractBalance] = useState(null);
  const [totalUsers, setTotalUsers] = useState('0');
  const [routerSafePool, setRouterSafePool] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [copied, setCopied] = useState('');
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [vrWallet, setVrWallet] = useState('');
  const [vrReferrer, setVrReferrer] = useState('');
  const [vrPackage, setVrPackage] = useState(0);
  const [vrLoading, setVrLoading] = useState(false);
  const [ewAmount, setEwAmount] = useState('');
  const [ewLoading, setEwLoading] = useState(false);
  const [rspAmount, setRspAmount] = useState('');
  const [rspLoading, setRspLoading] = useState(false);
  const [routerPct, setRouterPct] = useState('');
  const [missingPct, setMissingPct] = useState('');
  const [feeLoading, setFeeLoading] = useState(false);
  const [currentRouterPct, setCurrentRouterPct] = useState('');
  const [currentMissingPct, setCurrentMissingPct] = useState('');
  const [poolInfo, setPoolInfo] = useState(null);
  const [weeklyInfo, setWeeklyInfo] = useState(null);
  const [monthlyInfo, setMonthlyInfo] = useState(null);
  const [poolRoundIdx, setPoolRoundIdx] = useState(0);
  const [poolRoundAmt, setPoolRoundAmt] = useState('');
  const [weeklyRoundAmt, setWeeklyRoundAmt] = useState('');
  const [monthlyRoundAmt, setMonthlyRoundAmt] = useState('');
  const [poolLoading, setPoolLoading] = useState(false);
  const [wallets, setWallets] = useState({});
  const [walletLoading, setWalletLoading] = useState(false);
  const [newAdmin, setNewAdmin] = useState('');
  const [newTW1, setNewTW1] = useState('');
  const [newTW2, setNewTW2] = useState('');
  const [newRouter, setNewRouter] = useState('');
  const [newPoolsAddr, setNewPoolsAddr] = useState('');
  const [newMatrixAddr, setNewMatrixAddr] = useState('');
  const [newUsdtAddr, setNewUsdtAddr] = useState('');
  const [arWallet, setArWallet] = useState('');
  const [arReferrer, setArReferrer] = useState('');
  const [arLoading, setArLoading] = useState(false);
  const [eligUserId, setEligUserId] = useState('');
  const [eligPoolIdx, setEligPoolIdx] = useState(0);
  const [eligType, setEligType] = useState('pool'); // 'pool' | 'weekly' | 'monthly'
  const [eligAction, setEligAction] = useState(true); // true=add, false=remove
  const [eligLoading, setEligLoading] = useState(false);

  useEffect(() => {
    if (provider && isAdmin) loadOverview();
    // eslint-disable-next-line
  }, [provider, isAdmin]);

  async function loadOverview() {
    setLoading(true);
    try {
      const core = getCoreRead();
      if (!core) return;
      const [bal, tu, rs] = await Promise.all([core.getContractBalance(), core.getTotalUsers(), core.routerSafePool()]);
      setContractBalance(bal); setTotalUsers(tu.toString()); setRouterSafePool(rs);

      try {
        const iface = new ethers.utils.Interface(CoreABI);
        const currentBlock = await provider.getBlockNumber();
        const fromBlock = Math.max(0, currentBlock - 28800);
        const logs = await provider.getLogs({ address: config.contracts.core, topics: [iface.getEventTopic('UserRegistered')], fromBlock });
        const users = logs.map(log => { try { const p = iface.parseLog(log); return { userId: p.args.userId.toString(), wallet: p.args.wallet, referrerId: p.args.referrerId.toString() }; } catch { return null; } }).filter(Boolean).reverse();
        setRecentUsers(users);
      } catch { setRecentUsers([]); }

      if (isOwner) {
        try {
          const settings = await core.getRouterSettings();
          setCurrentRouterPct((settings.investmentRouterBps_.toNumber() / 100).toString());
          setCurrentMissingPct((settings.missingIncomeRouterBps_.toNumber() / 100).toString());
        } catch {}
        try {
          const [a, t1, t2, rw] = await Promise.all([core.admin(), core.teamWallet1(), core.teamWallet2(), core.routerWallet()]);
          setWallets({ admin: a, tw1: t1, tw2: t2, router: rw });
        } catch {}
      }

      const [pi, wi, mi] = await Promise.all([getAllPoolInfo(), getWeeklyRoyaltyInfo(), getMonthlyRoyaltyInfo()]);
      setPoolInfo(pi); setWeeklyInfo(wi); setMonthlyInfo(mi);
    } catch {}
    setLoading(false);
  }

  function copyText(text, label) { navigator.clipboard.writeText(text).then(() => { setCopied(label); setTimeout(() => setCopied(''), 1500); onNotify('Copied!', 'success'); }); }

  async function handleLookup() {
    if (!lookupId.trim()) return;
    setLookupLoading(true);
    const info = await getUserInfo(lookupId.trim());
    setLookupResult(info); setLookupLoading(false);
    if (!info) onNotify('User not found', 'error');
  }

  async function handleAdminRegister() {
    if (!arWallet.trim()) return onNotify('Enter wallet address', 'error');
    setArLoading(true);
    try {
      const core = new ethers.Contract(config.contracts.core, CoreABI, signer);
      const tx = await core.adminRegister(arWallet.trim(), arReferrer.trim() || '0');
      await tx.wait(); onNotify('Admin user registered (all packages)!', 'success'); setArWallet(''); setArReferrer(''); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setArLoading(false);
  }

  async function handleVirtualRegister() {
    if (!vrWallet.trim()) return onNotify('Enter wallet address', 'error');
    setVrLoading(true);
    try {
      const core = new ethers.Contract(config.contracts.core, CoreABI, signer);
      const tx = await core.adminRegisterVirtual(vrWallet.trim(), vrReferrer.trim() || '0', vrPackage);
      await tx.wait(); onNotify('Virtual user registered!', 'success'); setVrWallet(''); setVrReferrer(''); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setVrLoading(false);
  }

  async function handleEmergencyWithdraw() {
    if (!ewAmount || parseFloat(ewAmount) <= 0) return onNotify('Enter amount', 'error');
    setEwLoading(true);
    try {
      const core = new ethers.Contract(config.contracts.core, CoreABI, signer);
      const tx = await core.emergencyWithdraw(ethers.utils.parseUnits(ewAmount, 18));
      await tx.wait(); onNotify('Emergency withdraw success!', 'success'); setEwAmount(''); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setEwLoading(false);
  }

  async function handleRSPWithdraw() {
    if (!rspAmount || parseFloat(rspAmount) <= 0) return onNotify('Enter amount', 'error');
    setRspLoading(true);
    try {
      const core = new ethers.Contract(config.contracts.core, CoreABI, signer);
      const tx = await core.withdrawRouterSafePool(ethers.utils.parseUnits(rspAmount, 18));
      await tx.wait(); onNotify('Router Safe Pool withdrawn!', 'success'); setRspAmount(''); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setRspLoading(false);
  }

  async function handleSetFees() {
    setFeeLoading(true);
    try {
      const core = new ethers.Contract(config.contracts.core, CoreABI, signer);
      if (routerPct) { const tx = await core.setRouterPercentage(Math.round(parseFloat(routerPct) * 100)); await tx.wait(); onNotify(`Router fee set to ${routerPct}%`, 'success'); setRouterPct(''); }
      if (missingPct) { const tx = await core.setMissingIncomeRouterPercentage(Math.round(parseFloat(missingPct) * 100)); await tx.wait(); onNotify(`Missing income fee set to ${missingPct}%`, 'success'); setMissingPct(''); }
      loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setFeeLoading(false);
  }

  async function handleSetPoolRound() {
    if (!poolRoundAmt || parseFloat(poolRoundAmt) <= 0) return onNotify('Enter amount per user', 'error');
    setPoolLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      const tx = await pools.setPoolRoundIncome(poolRoundIdx, ethers.utils.parseUnits(poolRoundAmt, 18));
      await tx.wait(); onNotify(`${POOL_LABELS[poolRoundIdx]} round created!`, 'success'); setPoolRoundAmt(''); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setPoolLoading(false);
  }

  async function handleSetWeeklyRound() {
    if (!weeklyRoundAmt || parseFloat(weeklyRoundAmt) <= 0) return onNotify('Enter amount', 'error');
    setPoolLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      const tx = await pools.setWeeklyRoundIncome(ethers.utils.parseUnits(weeklyRoundAmt, 18));
      await tx.wait(); onNotify('Weekly round created!', 'success'); setWeeklyRoundAmt(''); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setPoolLoading(false);
  }

  async function handleSetMonthlyRound() {
    if (!monthlyRoundAmt || parseFloat(monthlyRoundAmt) <= 0) return onNotify('Enter amount', 'error');
    setPoolLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      const tx = await pools.setMonthlyRoundIncome(ethers.utils.parseUnits(monthlyRoundAmt, 18));
      await tx.wait(); onNotify('Monthly round created!', 'success'); setMonthlyRoundAmt(''); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setPoolLoading(false);
  }

  async function handleResetPool(poolIndex) {
    setPoolLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      const tx = await pools.resetPoolAccumulation(poolIndex);
      await tx.wait(); onNotify(`${POOL_LABELS[poolIndex]} accumulation reset!`, 'success'); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setPoolLoading(false);
  }

  async function handleResetWeekly() {
    setPoolLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      const tx = await pools.resetWeeklyRoyaltyAccumulation();
      await tx.wait(); onNotify('Weekly royalty accumulation reset!', 'success'); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setPoolLoading(false);
  }

  async function handleResetMonthly() {
    setPoolLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      const tx = await pools.resetMonthlyRoyaltyAccumulation();
      await tx.wait(); onNotify('Monthly royalty accumulation reset!', 'success'); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setPoolLoading(false);
  }

  async function handleResetAllDailyPools() {
    setPoolLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      const tx = await pools.resetAllPoolAccumulations();
      await tx.wait(); onNotify('All 6 daily share accumulations reset!', 'success'); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setPoolLoading(false);
  }

  async function handleResetAll() {
    setPoolLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      const tx = await pools.resetAllAccumulations();
      await tx.wait(); onNotify('All accumulations reset!', 'success'); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setPoolLoading(false);
  }

  async function handleSetEligibility() {
    if (!eligUserId.trim()) return onNotify('Enter User ID', 'error');
    setEligLoading(true);
    try {
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);
      let tx;
      if (eligType === 'pool') {
        tx = await pools.adminSetPoolEligibility(eligUserId.trim(), eligPoolIdx, eligAction);
      } else if (eligType === 'weekly') {
        tx = await pools.adminSetWeeklyRoyaltyEligibility(eligUserId.trim(), eligAction);
      } else {
        tx = await pools.adminSetMonthlyRoyaltyEligibility(eligUserId.trim(), eligAction);
      }
      await tx.wait();
      onNotify(`User ${eligAction ? 'added to' : 'removed from'} ${eligType === 'pool' ? POOL_LABELS[eligPoolIdx] : eligType + ' royalty'}!`, 'success');
      setEligUserId('');
      loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setEligLoading(false);
  }

  async function handleSetAdmin(value) {
    if (!value.trim()) return onNotify('Enter Admin address', 'error');
    setWalletLoading(true);
    try {
      const addr = value.trim();
      // Set admin in all 3 contracts
      const core = new ethers.Contract(config.contracts.core, CoreABI, signer);
      const matrix = new ethers.Contract(config.contracts.matrix, MatrixABI, signer);
      const pools = new ethers.Contract(config.contracts.pools, PoolsABI, signer);

      onNotify('Setting admin in Core...', 'pending', 0);
      const tx1 = await core.setAdmin(addr); await tx1.wait();

      onNotify('Setting admin in Matrix...', 'pending', 0);
      const tx2 = await matrix.setAdmin(addr); await tx2.wait();

      onNotify('Setting admin in Pools...', 'pending', 0);
      const tx3 = await pools.setAdmin(addr); await tx3.wait();

      onNotify('Admin updated in all 3 contracts!', 'success');
      setNewAdmin('');
      loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setWalletLoading(false);
  }

  async function handleSetWallet(fn, value, label) {
    if (!value.trim()) return onNotify(`Enter ${label} address`, 'error');
    setWalletLoading(true);
    try {
      const core = new ethers.Contract(config.contracts.core, CoreABI, signer);
      const tx = await core[fn](value.trim()); await tx.wait(); onNotify(`${label} updated!`, 'success'); loadOverview();
    } catch (err) { onNotify(getErrorMessage(err), 'error'); }
    setWalletLoading(false);
  }

  if (!account || !isAdmin) {
    return (<div className="page-container"><div className="connect-prompt"><h2>Access Denied</h2><p>Admin or Owner wallet required</p></div></div>);
  }

  const adminTabs = [{ id: 'overview', label: 'Overview' }, { id: 'lookup', label: 'Lookup' }, { id: 'users', label: 'Users' }, { id: 'pools', label: 'Daily Share' }, { id: 'settings', label: 'Settings' }];
  const ownerTabs = [{ id: 'fees', label: 'Fees' }, { id: 'wallets', label: 'Wallets' }, { id: 'contracts', label: 'Contracts' }];
  const tabs = isOwner ? [...adminTabs, ...ownerTabs] : adminTabs;

  if (loading) return <div className="page-container"><Loader text="Loading admin..." /></div>;

  return (
    <div className="page-container">
      <h2 className="page-title">{isOwner ? 'Owner Panel' : 'Admin Panel'}</h2>

      <div className="admin-tabs">
        {tabs.map(t => (<button key={t.id} className={`admin-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>))}
      </div>

      {/* OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="admin-section animate-in">
          {/* Stats Grid */}
          <div className="admin-stats-grid">
            <div className="admin-stat-card"><span className="asc-label">Contract Balance</span><span className="asc-value">${formatUSDTRaw(contractBalance)}</span></div>
            <div className="admin-stat-card"><span className="asc-label">Total Users</span><span className="asc-value">{totalUsers}</span></div>
            {isOwner && <div className="admin-stat-card"><span className="asc-label">Router Safe Pool</span><span className="asc-value">${formatUSDTRaw(routerSafePool)}</span></div>}
          </div>

          {/* Contract Addresses */}
          <div className="admin-card glass-card">
            <h4 className="admin-card-title">Contract Addresses</h4>
            <div className="addr-section">
              <span className="addr-label">Core</span>
              <div className="addr-row"><span className="addr-text">{config.contracts.core}</span><button className="btn-copy-sm" onClick={() => copyText(config.contracts.core, 'core')}>{copied === 'core' ? 'Copied' : 'Copy'}</button></div>
            </div>
            {isOwner && (<>
              <div className="addr-section">
                <span className="addr-label">Matrix</span>
                <div className="addr-row"><span className="addr-text">{config.contracts.matrix}</span><button className="btn-copy-sm" onClick={() => copyText(config.contracts.matrix, 'matrix')}>{copied === 'matrix' ? 'Copied' : 'Copy'}</button></div>
              </div>
              <div className="addr-section">
                <span className="addr-label">Pools</span>
                <div className="addr-row"><span className="addr-text">{config.contracts.pools}</span><button className="btn-copy-sm" onClick={() => copyText(config.contracts.pools, 'pools')}>{copied === 'pools' ? 'Copied' : 'Copy'}</button></div>
              </div>
            </>)}
          </div>

          {/* Wallet Addresses - Owner only */}
          {isOwner && wallets.admin && (
            <div className="admin-card glass-card" style={{marginTop: 12}}>
              <h4 className="admin-card-title">Wallet Addresses</h4>
              {[
                { label: 'Owner', value: account },
                { label: 'Admin', value: wallets.admin },
                { label: 'Team Wallet 1', value: wallets.tw1 },
                { label: 'Team Wallet 2', value: wallets.tw2 },
                { label: 'Router Wallet', value: wallets.router },
              ].map((w, i) => (
                <div key={i} className="addr-section">
                  <span className="addr-label">{w.label}</span>
                  <div className="addr-row"><span className="addr-text">{w.value}</span><button className="btn-copy-sm" onClick={() => copyText(w.value, w.label)}>{copied === w.label ? 'Copied' : 'Copy'}</button></div>
                </div>
              ))}
            </div>
          )}

          {/* Fee Info - Owner only */}
          {isOwner && (
            <div className="admin-card glass-card" style={{marginTop: 12}}>
              <h4 className="admin-card-title">Current Fee Settings</h4>
              <div className="ov-fee-grid">
                <div className="ov-fee-item"><span className="ov-fee-label">Router Fee</span><span className="ov-fee-value">{currentRouterPct}%</span></div>
                <div className="ov-fee-item"><span className="ov-fee-label">Missing Income Router</span><span className="ov-fee-value">{currentMissingPct}%</span></div>
                <div className="ov-fee-item"><span className="ov-fee-label">Income Deduction</span><span className="ov-fee-value">10%</span></div>
                <div className="ov-fee-item"><span className="ov-fee-label">Pool Claim Deduction</span><span className="ov-fee-value">10%</span></div>
              </div>
            </div>
          )}

          {/* 24h Registrations */}
          <div className="admin-card glass-card" style={{marginTop: 12}}>
            <h4 className="admin-card-title">24h Registrations ({recentUsers.length})</h4>
            {recentUsers.length > 0 ? (
              <div className="recent-list">
                {recentUsers.slice(0, 20).map((u, i) => (<div key={i} className="recent-item"><span className="ri-id">#{u.userId}</span><span className="ri-wallet">{shortenAddress(u.wallet)}</span><span className="ri-ref">Ref: #{u.referrerId}</span></div>))}
              </div>
            ) : <p className="empty-text">No registrations in last 24h</p>}
          </div>
        </div>
      )}

      {/* LOOKUP */}
      {activeTab === 'lookup' && (
        <div className="admin-section animate-in">
          <div className="admin-card glass-card">
            <h4 className="admin-card-title">Lookup User</h4>
            <div className="admin-form-row"><input className="form-input" placeholder="User ID" value={lookupId} onChange={e => setLookupId(e.target.value)} /><button className="btn-gradient btn-sm" onClick={handleLookup} disabled={lookupLoading}>{lookupLoading ? '...' : 'Search'}</button></div>
            {lookupResult && (
              <div className="lookup-result">
                <div className="lr-row"><span>Wallet</span><span>{shortenAddress(lookupResult.wallet)}</span></div>
                <div className="lr-row"><span>Referrer</span><span>#{lookupResult.referrerId}</span></div>
                <div className="lr-row"><span>Direct Refs</span><span>{lookupResult.totalDirectRefs}</span></div>
                <div className="lr-row"><span>Total Income</span><span>${formatUSDTRaw(lookupResult.totalIncomeEarned)}</span></div>
                <div className="lr-row"><span>Type</span><span>{['Normal','Admin','Virtual'][lookupResult.userType]}</span></div>
                <div className="lr-packages">{lookupResult.packageActive.map((a, i) => (<span key={i} className={`lr-pkg ${a ? 'active' : ''}`}>{PACKAGE_LABELS[i]}</span>))}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* USERS */}
      {activeTab === 'users' && (
        <div className="admin-section animate-in">
          {/* Admin Register - Owner only */}
          {isOwner && (
            <div className="admin-card glass-card" style={{marginBottom: 12}}>
              <h4 className="admin-card-title">Admin Register (All Packages, No USDT)</h4>
              <div className="form-group"><label>Wallet Address</label><input className="form-input" placeholder="0x..." value={arWallet} onChange={e => setArWallet(e.target.value)} /></div>
              <div className="form-group"><label>Referrer ID (optional)</label><input className="form-input" placeholder="Referrer ID" value={arReferrer} onChange={e => setArReferrer(e.target.value)} /></div>
              <button className="btn-gradient btn-full" onClick={handleAdminRegister} disabled={arLoading}>{arLoading ? 'Registering...' : 'Register Admin User'}</button>
              <p className="admin-hint" style={{marginTop: 8}}>Registers with admin privileges and all 7 packages active</p>
            </div>
          )}

          {/* Virtual Register - Both admin and owner */}
          <div className="admin-card glass-card">
            <h4 className="admin-card-title">Virtual Register (No USDT)</h4>
            <div className="form-group"><label>Wallet Address</label><input className="form-input" placeholder="0x..." value={vrWallet} onChange={e => setVrWallet(e.target.value)} /></div>
            <div className="form-group"><label>Referrer ID (optional)</label><input className="form-input" placeholder="Referrer ID" value={vrReferrer} onChange={e => setVrReferrer(e.target.value)} /></div>
            <div className="form-group"><label>Up to Package</label>
              <div className="pkg-select-grid">{PACKAGE_NAMES.map((name, i) => (<button key={i} className={`pkg-select-btn ${vrPackage >= i ? 'selected' : ''}`} onClick={() => setVrPackage(i)}><span className="pkg-name">{name}</span><span className="pkg-price">{PACKAGE_LABELS[i]}</span></button>))}</div>
            </div>
            <button className="btn-gradient btn-full" onClick={handleVirtualRegister} disabled={vrLoading}>{vrLoading ? 'Registering...' : 'Register Virtual'}</button>
          </div>
        </div>
      )}

      {/* POOLS */}
      {activeTab === 'pools' && (
        <div className="admin-section animate-in">
          <h4 className="admin-card-title" style={{marginBottom: 12}}>Daily Share Details</h4>
          {poolInfo && (
            <div className="pool-admin-list">
              {POOL_LABELS.map((label, i) => {
                const accum = poolInfo.accumulations[i]; const eligible = poolInfo.eligibleCounts[i]; const round = poolInfo.currentRounds[i];
                const perUser = parseInt(eligible) > 0 && accum ? (parseFloat(formatUSDTRaw(accum)) / parseInt(eligible)).toFixed(4) : '0';
                return (
                  <div key={i} className="pool-admin-card glass-card">
                    <div className="pac-header"><span className="pac-name">{label}</span><span className="pac-round">Round {round}</span></div>
                    <div className="pac-stats">
                      <div className="pac-stat"><span className="pac-stat-label">Balance</span><span className="pac-stat-value">${formatUSDTRaw(accum)}</span></div>
                      <div className="pac-stat"><span className="pac-stat-label">Eligible</span><span className="pac-stat-value">{eligible}</span></div>
                      <div className="pac-stat"><span className="pac-stat-label">Per User</span><span className="pac-stat-value">${perUser}</span></div>
                    </div>
                    <button className="btn-reset" onClick={() => handleResetPool(i)} disabled={poolLoading}>Reset</button>
                  </div>
                );
              })}
            </div>
          )}

          <button className="btn-reset-daily" onClick={handleResetAllDailyPools} disabled={poolLoading}>
            {poolLoading ? 'Resetting...' : 'Reset All 6 Daily Share Accumulations'}
          </button>

          <div className="pool-admin-list" style={{marginTop: 16}}>
            {weeklyInfo && (<div className="pool-admin-card glass-card"><div className="pac-header"><span className="pac-name">Weekly Royalty</span><span className="pac-round">Round {weeklyInfo.currentRound}</span></div><div className="pac-stats"><div className="pac-stat"><span className="pac-stat-label">Balance</span><span className="pac-stat-value">${formatUSDTRaw(weeklyInfo.accumulation)}</span></div><div className="pac-stat"><span className="pac-stat-label">Eligible</span><span className="pac-stat-value">{weeklyInfo.eligibleCount}</span></div><div className="pac-stat"><span className="pac-stat-label">Per User</span><span className="pac-stat-value">${parseInt(weeklyInfo.eligibleCount) > 0 ? (parseFloat(formatUSDTRaw(weeklyInfo.accumulation))/parseInt(weeklyInfo.eligibleCount)).toFixed(4) : '0'}</span></div></div><button className="btn-reset" onClick={handleResetWeekly} disabled={poolLoading}>Reset</button></div>)}
            {monthlyInfo && (<div className="pool-admin-card glass-card"><div className="pac-header"><span className="pac-name">Monthly Royalty</span><span className="pac-round">Round {monthlyInfo.currentRound}</span></div><div className="pac-stats"><div className="pac-stat"><span className="pac-stat-label">Balance</span><span className="pac-stat-value">${formatUSDTRaw(monthlyInfo.accumulation)}</span></div><div className="pac-stat"><span className="pac-stat-label">Eligible</span><span className="pac-stat-value">{monthlyInfo.eligibleCount}</span></div><div className="pac-stat"><span className="pac-stat-label">Per User</span><span className="pac-stat-value">${parseInt(monthlyInfo.eligibleCount) > 0 ? (parseFloat(formatUSDTRaw(monthlyInfo.accumulation))/parseInt(monthlyInfo.eligibleCount)).toFixed(4) : '0'}</span></div></div><button className="btn-reset" onClick={handleResetMonthly} disabled={poolLoading}>Reset</button></div>)}
          </div>

          <button className="btn-reset-all" onClick={handleResetAll} disabled={poolLoading}>
            {poolLoading ? 'Resetting...' : 'Reset All Accumulations'}
          </button>

          <div className="admin-card glass-card" style={{marginTop: 16}}>
            <h4 className="admin-card-title">Set Daily Share Round Income</h4>
            <div className="form-group"><label>Pool</label>
              <div className="pkg-select-grid" style={{gridTemplateColumns:'repeat(3,1fr)'}}>{POOL_LABELS.map((l, i) => (<button key={i} className={`pkg-select-btn ${poolRoundIdx === i ? 'selected' : ''}`} onClick={() => setPoolRoundIdx(i)}><span className="pkg-price" style={{fontSize:11}}>{l}</span></button>))}</div>
            </div>
            <div className="admin-form-row"><input className="form-input" placeholder="Amount per user (USDT)" value={poolRoundAmt} onChange={e => setPoolRoundAmt(e.target.value)} /><button className="btn-gradient btn-sm" onClick={handleSetPoolRound} disabled={poolLoading}>Set</button></div>
          </div>
          <div className="admin-card glass-card" style={{marginTop: 12}}><h4 className="admin-card-title">Weekly Round</h4><div className="admin-form-row"><input className="form-input" placeholder="Amount per user (USDT)" value={weeklyRoundAmt} onChange={e => setWeeklyRoundAmt(e.target.value)} /><button className="btn-gradient btn-sm" onClick={handleSetWeeklyRound} disabled={poolLoading}>Set</button></div></div>
          <div className="admin-card glass-card" style={{marginTop: 12}}><h4 className="admin-card-title">Monthly Round</h4><div className="admin-form-row"><input className="form-input" placeholder="Amount per user (USDT)" value={monthlyRoundAmt} onChange={e => setMonthlyRoundAmt(e.target.value)} /><button className="btn-gradient btn-sm" onClick={handleSetMonthlyRound} disabled={poolLoading}>Set</button></div></div>

          {/* Add/Remove User Eligibility */}
          {isOwner && (
            <div className="admin-card glass-card" style={{marginTop: 16}}>
              <h4 className="admin-card-title">Manage User Pool Eligibility</h4>
              <div className="form-group">
                <label>User ID</label>
                <input className="form-input" placeholder="Enter User ID" value={eligUserId} onChange={e => setEligUserId(e.target.value)} />
              </div>
              <div className="form-group">
                <label>Pool Type</label>
                <div className="elig-type-btns">
                  <button className={`elig-type-btn ${eligType === 'pool' ? 'active' : ''}`} onClick={() => setEligType('pool')}>Daily Share</button>
                  <button className={`elig-type-btn ${eligType === 'weekly' ? 'active' : ''}`} onClick={() => setEligType('weekly')}>Weekly</button>
                  <button className={`elig-type-btn ${eligType === 'monthly' ? 'active' : ''}`} onClick={() => setEligType('monthly')}>Monthly</button>
                </div>
              </div>
              {eligType === 'pool' && (
                <div className="form-group">
                  <label>Select Pool</label>
                  <div className="pkg-select-grid" style={{gridTemplateColumns:'repeat(3,1fr)'}}>
                    {POOL_LABELS.map((l, i) => (
                      <button key={i} className={`pkg-select-btn ${eligPoolIdx === i ? 'selected' : ''}`} onClick={() => setEligPoolIdx(i)}>
                        <span className="pkg-price" style={{fontSize:11}}>{l}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Action</label>
                <div className="elig-type-btns">
                  <button className={`elig-type-btn ${eligAction ? 'active add' : ''}`} onClick={() => setEligAction(true)}>Add (Eligible)</button>
                  <button className={`elig-type-btn ${!eligAction ? 'active remove' : ''}`} onClick={() => setEligAction(false)}>Remove (Ineligible)</button>
                </div>
              </div>
              <button className="btn-gradient btn-full" onClick={handleSetEligibility} disabled={eligLoading}>
                {eligLoading ? 'Processing...' : `${eligAction ? 'Add' : 'Remove'} User ${eligAction ? 'to' : 'from'} ${eligType === 'pool' ? POOL_LABELS[eligPoolIdx] : eligType === 'weekly' ? 'Weekly Royalty' : 'Monthly Royalty'}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* SETTINGS */}
      {activeTab === 'settings' && (
        <div className="admin-section animate-in">
          <div className="admin-card glass-card"><h4 className="admin-card-title">Contract Balance</h4><p className="admin-value-display">${formatUSDTRaw(contractBalance)} USDT</p></div>
          <div className="admin-card glass-card" style={{marginTop: 12}}>
            <h4 className="admin-card-title">Emergency Withdraw</h4>
            <div className="admin-form-row"><input className="form-input" placeholder="Amount (USDT)" value={ewAmount} onChange={e => setEwAmount(e.target.value)} /><button className="btn-gradient btn-sm" onClick={handleEmergencyWithdraw} disabled={ewLoading} style={{background:'linear-gradient(135deg, #ef4444, #dc2626)'}}>{ewLoading ? '...' : 'Withdraw'}</button></div>
          </div>
          {isOwner && (<div className="admin-card glass-card" style={{marginTop: 12}}><h4 className="admin-card-title">Router Safe Pool: ${formatUSDTRaw(routerSafePool)}</h4><div className="admin-form-row"><input className="form-input" placeholder="Amount (USDT)" value={rspAmount} onChange={e => setRspAmount(e.target.value)} /><button className="btn-gradient btn-sm" onClick={handleRSPWithdraw} disabled={rspLoading}>{rspLoading ? '...' : 'Withdraw'}</button></div></div>)}
        </div>
      )}

      {/* FEES (Owner) */}
      {activeTab === 'fees' && isOwner && (
        <div className="admin-section animate-in">
          <div className="admin-card glass-card">
            <h4 className="admin-card-title">Fee Settings</h4>
            <div className="form-group"><label>Router Fee (current: {currentRouterPct}%)</label><div className="admin-form-row"><input className="form-input" placeholder="e.g. 1" value={routerPct} onChange={e => setRouterPct(e.target.value)} /><span className="input-suffix">%</span></div></div>
            <div className="form-group"><label>Missing Income Router Fee (current: {currentMissingPct}%)</label><div className="admin-form-row"><input className="form-input" placeholder="e.g. 2" value={missingPct} onChange={e => setMissingPct(e.target.value)} /><span className="input-suffix">%</span></div></div>
            <button className="btn-gradient btn-full" onClick={handleSetFees} disabled={feeLoading}>{feeLoading ? 'Updating...' : 'Update Fees'}</button>
          </div>
        </div>
      )}

      {/* WALLETS (Owner) */}
      {activeTab === 'wallets' && isOwner && (
        <div className="admin-section animate-in">
          <div className="admin-card glass-card">
            <h4 className="admin-card-title">Current Wallets</h4>
            {[
              { label: 'Admin', value: wallets.admin, key: 'w-admin' },
              { label: 'Team Wallet 1', value: wallets.tw1, key: 'w-tw1' },
              { label: 'Team Wallet 2', value: wallets.tw2, key: 'w-tw2' },
              { label: 'Router Wallet', value: wallets.router, key: 'w-router' },
            ].map((w, i) => (
              <div key={i} className="addr-section">
                <span className="addr-label">{w.label}</span>
                <div className="addr-row"><span className="addr-text">{w.value}</span><button className="btn-copy-sm" onClick={() => copyText(w.value, w.key)}>{copied === w.key ? 'Copied' : 'Copy'}</button></div>
              </div>
            ))}
          </div>
          {/* Admin - updates all 3 contracts */}
          <div className="admin-card glass-card" style={{marginTop:12}}>
            <h4 className="admin-card-title">Set Admin <span className="admin-hint-inline">(Core + Matrix + Pools)</span></h4>
            <div className="admin-form-row"><input className="form-input" placeholder="0x..." value={newAdmin} onChange={e => setNewAdmin(e.target.value)} /><button className="btn-gradient btn-sm" onClick={() => handleSetAdmin(newAdmin)} disabled={walletLoading}>{walletLoading ? '...' : 'Set All 3'}</button></div>
          </div>
          {/* Other wallets - Core only */}
          {[{label:'Team Wallet 1',state:newTW1,setter:setNewTW1,fn:'setTeamWallet1'},{label:'Team Wallet 2',state:newTW2,setter:setNewTW2,fn:'setTeamWallet2'},{label:'Router Wallet',state:newRouter,setter:setNewRouter,fn:'setRouterWallet'}].map((w,i) => (
            <div key={i} className="admin-card glass-card" style={{marginTop:12}}><h4 className="admin-card-title">Set {w.label}</h4><div className="admin-form-row"><input className="form-input" placeholder="0x..." value={w.state} onChange={e => w.setter(e.target.value)} /><button className="btn-gradient btn-sm" onClick={() => handleSetWallet(w.fn, w.state, w.label)} disabled={walletLoading}>Set</button></div></div>
          ))}
        </div>
      )}

      {/* CONTRACTS (Owner) */}
      {activeTab === 'contracts' && isOwner && (
        <div className="admin-section animate-in">
          <div className="admin-card glass-card">
            <h4 className="admin-card-title">Current Contract Addresses</h4>
            {[
              { label: 'Core', value: config.contracts.core, key: 'c-core' },
              { label: 'Matrix', value: config.contracts.matrix, key: 'c-matrix' },
              { label: 'Pools', value: config.contracts.pools, key: 'c-pools' },
              { label: 'USDT', value: config.usdt || 'From contract', key: 'c-usdt' },
            ].map((c, i) => (
              <div key={i} className="addr-section">
                <span className="addr-label">{c.label}</span>
                <div className="addr-row"><span className="addr-text">{c.value}</span><button className="btn-copy-sm" onClick={() => copyText(c.value, c.key)}>{copied === c.key ? 'Copied' : 'Copy'}</button></div>
              </div>
            ))}
          </div>
          <div className="admin-card glass-card" style={{marginTop:12}}><h4 className="admin-card-title">Set Pools Contract</h4><div className="admin-form-row"><input className="form-input" placeholder="0x..." value={newPoolsAddr} onChange={e => setNewPoolsAddr(e.target.value)} /><button className="btn-gradient btn-sm" onClick={() => handleSetWallet('setPoolsContract', newPoolsAddr, 'Pools')} disabled={walletLoading}>Set</button></div></div>
          <div className="admin-card glass-card" style={{marginTop:12}}><h4 className="admin-card-title">Set Matrix Contract</h4><div className="admin-form-row"><input className="form-input" placeholder="0x..." value={newMatrixAddr} onChange={e => setNewMatrixAddr(e.target.value)} /><button className="btn-gradient btn-sm" onClick={() => handleSetWallet('setMatrixContract', newMatrixAddr, 'Matrix')} disabled={walletLoading}>Set</button></div></div>
          <div className="admin-card glass-card" style={{marginTop:12}}><h4 className="admin-card-title">Set USDT Token</h4><div className="admin-form-row"><input className="form-input" placeholder="0x..." value={newUsdtAddr} onChange={e => setNewUsdtAddr(e.target.value)} /><button className="btn-gradient btn-sm" onClick={() => handleSetWallet('setUSDTToken', newUsdtAddr, 'USDT')} disabled={walletLoading}>Set</button></div></div>
        </div>
      )}
    </div>
  );
}
